'use client'

import { visibleHomepageStats } from '../../lib/facts'
import './home-v2.css'

// Final figures come from src/lib/facts.ts and are rendered straight into the
// server HTML. The old count-up started at 0, so crawlers (and anyone without
// JS) saw "0+". A tile whose fact is null is not rendered at all.
function Stat({ stat, index }) {
  return (
    <div
      className="hv2-stat"
      // Arrives out of depth (styles/reveal.css) and pushes further forward
      // on hover (home-v2.css), the same vocabulary every other surface on
      // the page uses.
      data-rv="0.5"
      data-rv-fx="depth"
      style={{ '--rv-p': '900px', '--rv-y': '16px', '--rv-z': '-240px', '--rv-dur': '.8s', '--rv-delay': `${index * 0.08}s` }}
    >
      <span className="hv2-stat-value">
        {stat.value}
        {stat.suffix ? <span className="hv2-stat-suffix">{stat.suffix}</span> : null}
      </span>
      <span className="hv2-stat-label">{stat.label}</span>
    </div>
  )
}

// w/h: each file's real pixel size, so the browser reserves the right box
// before it loads (Lighthouse "unsized images"). The logos show at most 42px
// tall, so the "-h84" files are 2x WebP copies of the originals (about a
// third of the bytes; this strip loads right under the hero). Two originals
// stayed, where WebP saved nothing. A second Octopi Digital logo on a host
// that no longer serves it was removed.
const PROVIDED_LOGOS = [
  { name: 'Octopi Digital LLC', image_url: '/images/external/69c375c8c1440367502c384a-aac3c605e0-h84.webp', w: 210, h: 84 },
  { name: 'One Two Six Design', image_url: '/images/external/69194ff9fd6d73eb0926fe35-7b8f32bb22-h84.webp', w: 84, h: 84 },
  { name: 'Campo', image_url: '/images/external/69194ff8fd6d735af326fe25-a405bb0f0a-h84.webp', w: 167, h: 43 },
  { name: 'Unique Workspace', image_url: '/images/external/68c3c277462dd3cd96c5d5ae-db61de3469-h84.webp', w: 250, h: 81 },
  { name: 'JackRyanAI', image_url: '/images/external/6a4b96c8631efcd7dac0d929-b76734fd51-h84.webp', w: 137, h: 84 },
  { name: 'Tetty Roofing', image_url: '/images/external/Screenshot-2026-02-26-at-12-56-22-brand--bed33f9eb2.png', w: 252, h: 104 },
  { name: 'Tree Care Marketing Solutions', image_url: '/images/external/TM-TCMScolor-26b6ccc118-h84.webp', w: 205, h: 84 },
  { name: 'Growtheon', image_url: '/images/external/growtheon-logo-VdXHpiYr-c9f603b5d8-h84.webp', w: 256, h: 84 },
  { name: 'GLScape', image_url: '/images/external/69194ff8fd6d737e0426fe26-e1a93f7b7e-h84.webp', w: 211, h: 57 },
  { name: 'Monkeymans', image_url: '/images/external/logo-1da131cbde.svg', w: 244, h: 88 },
  { name: 'OpsROi', image_url: 'https://opsroi.com/wp-content/uploads/2026/01/OPS-ROI-LOGO.svg', w: 18, h: 18 },
  { name: 'Astral Systems', image_url: '/images/external/astral-logo-a839ea1882.svg', w: 108, h: 42 },
  { name: 'Emerald Consulting', image_url: '/images/external/DarkGreenandGoldElegantFinancialConsulta-eb5318583c-h84.webp', w: 84, h: 84 },
  { name: 'Speak & Play', image_url: '/images/external/68cfd927a56aee65837c72e0-f09b9885d0-h84.webp', w: 84, h: 84 },
  { name: 'More Jobs AI', image_url: '/images/external/69af9deaddc8c75481d30f20-1996aa2ea2-h84.webp', w: 220, h: 84 },
  { name: 'Top Talent HQ', image_url: '/images/external/6a4b91572e40aeae01566986-fe26ec5fe3-h84.webp', w: 196, h: 84 },
  { name: 'Dat9', image_url: '/images/external/68a4e66fb130b35c7f196e20-d4f80d13d4-h84.webp', w: 248, h: 84 },
  { name: 'Floor Daddy CRM', image_url: '/images/external/Floor-Daddy-Website-Header-Logo-d067f03395.webp', w: 400, h: 125 },
]

export default function TrustBandV2() {
  const stats = visibleHomepageStats()

  const withImages = PROVIDED_LOGOS.filter((l) => l?.image_url)
  // One loop copy has to be at least as wide as the row, or the -50% keyframe
  // exposes a gap. Repeat the set until it is long enough, then duplicate that
  // whole copy so the loop point stays seamless.
  const copies = withImages.length ? Math.max(1, Math.ceil(10 / withImages.length)) : 0
  const oneCopy = Array.from({ length: copies }, () => withImages).flat()
  const track = oneCopy.length ? [...oneCopy, ...oneCopy] : []

  return (
    <>
      {track.length ? (
        <section className="hv2 hv2-marquee-section" aria-label="Brands we work with">
          <div className="hv2-inner">
            <p className="hv2-marquee-label">Trusted by agencies and growth-focused businesses</p>
            <div className="hv2-marquee">
              <div className="hv2-marquee-track">
                {track.map((logo, i) => (
                  <span className="hv2-marquee-item" key={`${logo.id || logo.name}-${i}`} aria-hidden={i >= withImages.length}>
                    {/* Logo URLs come from the admin and some point at hosts
                        that have since gone away. Drop the tile rather than
                        parade a broken-image glyph across the marquee. */}
                    <img
                      src={logo.image_url}
                      alt={`${logo.name} logo`}
                      width={logo.w}
                      height={logo.h}
                      loading="lazy"
                      decoding="async"
                      onError={(e) => { e.currentTarget.parentElement.style.display = 'none' }}
                    />
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {stats.length ? (
      <section className="hv2 hv2-section is-paper">
        <div className="hv2-inner">
          <div className="hv2-stats" data-rv="0.3">
            {stats.map((stat, i) => (
              <Stat key={stat.label} stat={stat} index={i} />
            ))}
          </div>
        </div>
      </section>
      ) : null}
    </>
  )
}
